import React from 'react';

/**
 * LanguageDisplayToggle
 *
 * Compact two-state pill for the transcript modal's display-language switch.
 * Historically used only for Hindi (EN / हिं) — now generalized so the modal
 * can label it Original / English for any non-English interview language.
 *
 * The internal value shape is still 'en' | 'hi' so no other caller has to
 * change; 'en' means "show English translation", 'hi' means "show source as
 * stored". Pass `labels` to override the button text.
 *
 * Usage:
 *   <LanguageDisplayToggle value={lang} onChange={setLang} />
 *   <LanguageDisplayToggle
 *     value={lang} onChange={setLang}
 *     labels={{ en: 'English', hi: 'Original' }}
 *   />
 */
const DEFAULT_LABELS = { en: 'EN', hi: 'हिं' };

const LanguageDisplayToggle = ({
  value = 'en',
  onChange,
  className = '',
  size = 'sm',
  /** 'compact' = small pill; 'voiceControl' = same shell as AI Voice dropdown trigger */
  variant = 'compact',
  labels,
}) => {
  const finalLabels = { ...DEFAULT_LABELS, ...(labels || {}) };
  const handleSet = (next) => {
    if (typeof onChange === 'function' && next !== value) {
      onChange(next);
    }
  };

  const heightClass = size === 'sm' ? 'h-7' : 'h-8';
  const padClass = size === 'sm' ? 'px-2 text-xs' : 'px-3 text-sm';

  if (variant === 'voiceControl') {
    const shellFocused = value === 'hi';
    return (
      <div
        className={`w-full flex items-stretch rounded-lg border-2 transition-all duration-200 bg-white overflow-hidden ${
          shellFocused
            ? 'border-[#007BFF] ring-2 ring-[#007BFF]/20'
            : 'border-gray-200 hover:border-gray-300'
        } ${className}`}
        role="group"
        aria-label="View language"
      >
        <button
          type="button"
          onClick={() => handleSet('en')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1 px-2 text-sm font-semibold transition-colors ${
            value === 'en'
              ? 'bg-[#007BFF]/10 text-[#007BFF]'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
          aria-pressed={value === 'en'}
        >
          {finalLabels.en}
        </button>
        <div className="w-px shrink-0 bg-gray-200 self-stretch my-1" aria-hidden />
        <button
          type="button"
          onClick={() => handleSet('hi')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1 px-2 text-sm font-semibold transition-colors ${
            value === 'hi'
              ? 'bg-[#007BFF]/10 text-[#007BFF]'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
          aria-pressed={value === 'hi'}
        >
          {finalLabels.hi}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center rounded-md border border-gray-200 bg-white p-0.5 ${heightClass} ${className}`}
      role="group"
      aria-label="View language"
    >
      <button
        type="button"
        onClick={() => handleSet('en')}
        className={`inline-flex items-center justify-center rounded font-medium transition-colors ${padClass} ${
          value === 'en'
            ? 'bg-[#007BFF] text-white'
            : 'text-gray-600 hover:text-gray-900'
        }`}
        aria-pressed={value === 'en'}
      >
        {finalLabels.en}
      </button>
      <button
        type="button"
        onClick={() => handleSet('hi')}
        className={`inline-flex items-center justify-center rounded font-medium transition-colors ${padClass} ${
          value === 'hi'
            ? 'bg-[#007BFF] text-white'
            : 'text-gray-600 hover:text-gray-900'
        }`}
        aria-pressed={value === 'hi'}
      >
        {finalLabels.hi}
      </button>
    </div>
  );
};

export default LanguageDisplayToggle;
